import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { sanitizeHtml } from '@/lib/sanitize';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Check,
  Loader2,
  PenLine,
  ScrollText,
  UserPen,
  X as XIcon,
  Clock,
} from 'lucide-react';
import EditAuthorDialog from '@/components/EditAuthorDialog';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function AdminPendingStories() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(null);
  const [previewContent, setPreviewContent] = useState('');
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/stories/pending');
      setItems(data);
    } catch (err) {
      toast.error('Erro ao carregar fila de aprovação.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openPreview = async (story) => {
    setPreviewing(story);
    setPreviewContent('');
    try {
      // The pending list omits content for payload size — fetch the full story.
      const { data } = await api.get(`/stories/${story.id}`);
      setPreviewContent(data.content || '');
    } catch (err) {
      toast.error('Não foi possível carregar o conteúdo.');
    }
  };

  const approve = async (storyId) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/stories/${storyId}/approve`);
      toast.success('Conto aprovado e publicado.');
      setPreviewing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao aprovar conto.');
    } finally {
      setActionLoading(false);
    }
  };

  const reject = async () => {
    if (!rejecting) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/stories/${rejecting.id}/reject`, {
        reason: rejectReason.trim() || null,
      });
      toast.success('Conto rejeitado.');
      setRejecting(null);
      setRejectReason('');
      setPreviewing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao rejeitar conto.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            data-testid="pending-stories-back"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao painel
          </button>

          <header className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-ruby to-ruby-light flex items-center justify-center">
              <ScrollText className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Aprovação de Contos</h1>
              <p className="text-muted-foreground">
                Histórias submetidas por leitores a aguardar revisão
              </p>
            </div>
          </header>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-ruby" />
            </div>
          ) : items.length === 0 ? (
            <Card className="bg-surface border-border" data-testid="pending-empty">
              <CardContent className="py-16 text-center">
                <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Não há submissões pendentes
                </h2>
                <p className="text-muted-foreground">
                  Quando um leitor submeter um conto, aparece aqui para revisão.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {items.map((s) => (
                <Card
                  key={s.id}
                  className="bg-surface border-border hover:border-ruby/40 transition-colors"
                  data-testid={`pending-story-${s.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-lg font-semibold text-foreground">
                            {s.title}
                          </h3>
                          <Badge variant="outline" className="text-amber-300 border-amber-300/40">
                            <Clock className="w-3 h-3 mr-1" />
                            Pendente
                          </Badge>
                          {(s.categories || []).map((c) => (
                            <Badge key={c} variant="secondary" className="text-xs">
                              {c}
                            </Badge>
                          ))}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {s.synopsis}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Por <strong className="text-foreground">{s.author_name || '—'}</strong> · {formatDate(s.created_at)} · {s.reading_time || 1} min
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          variant="outline"
                          onClick={() => openPreview(s)}
                          data-testid={`pending-preview-${s.id}`}
                        >
                          <PenLine className="w-4 h-4 mr-2" />
                          Pré-visualizar
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setEditingAuthor(s)}
                          data-testid={`pending-edit-author-${s.id}`}
                          title="Editar autor"
                        >
                          <UserPen className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => approve(s.id)}
                          disabled={actionLoading}
                          data-testid={`pending-approve-${s.id}`}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Aprovar
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setRejecting(s)}
                          disabled={actionLoading}
                          data-testid={`pending-reject-${s.id}`}
                          className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                        >
                          <XIcon className="w-4 h-4 mr-2" />
                          Rejeitar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Preview dialog */}
      <Dialog open={!!previewing} onOpenChange={(o) => !o && setPreviewing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {previewing && (
            <>
              <DialogHeader>
                <DialogTitle>{previewing.title}</DialogTitle>
                <DialogDescription>
                  Por <strong>{previewing.author_name}</strong> — {previewing.synopsis}
                </DialogDescription>
              </DialogHeader>
              {previewing.cover_image_description && (
                <div
                  className="rounded-lg border border-ruby/30 bg-ruby/5 p-4 space-y-1"
                  data-testid="admin-cover-image-description"
                >
                  <p className="text-xs uppercase tracking-wide font-semibold text-ruby">
                    Descrição sugerida para a imagem de capa
                  </p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {previewing.cover_image_description}
                  </p>
                  <p className="text-xs text-muted-foreground italic pt-1">
                    Referência editorial — a equipa Intimate criará ou selecionará a imagem de capa final.
                  </p>
                </div>
              )}
              <div
                className="reader-content text-muted-foreground"
                style={{ lineHeight: '1.8' }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewContent) }}
              />
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setRejecting(previewing)}
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                >
                  <XIcon className="w-4 h-4 mr-2" />
                  Rejeitar
                </Button>
                <Button
                  onClick={() => approve(previewing.id)}
                  disabled={actionLoading}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Aprovar e publicar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog
        open={!!rejecting}
        onOpenChange={(o) => {
          if (!o) {
            setRejecting(null);
            setRejectReason('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400">Rejeitar conto</DialogTitle>
            <DialogDescription>
              {rejecting && (
                <>
                  Vais rejeitar <strong className="text-foreground">{rejecting.title}</strong>. Podes opcionalmente registar a razão para auditoria.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Razão (opcional)"
            data-testid="reject-reason-input"
            className="min-h-[100px]"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRejecting(null)} disabled={actionLoading}>
              Cancelar
            </Button>
            <Button
              onClick={reject}
              disabled={actionLoading}
              data-testid="reject-confirm"
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A rejeitar...
                </>
              ) : (
                <>
                  <XIcon className="w-4 h-4 mr-2" />
                  Confirmar rejeição
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditAuthorDialog
        open={!!editingAuthor}
        onOpenChange={(o) => !o && setEditingAuthor(null)}
        story={editingAuthor || {}}
        onSaved={(updated) => {
          setItems((arr) => arr.map((it) => it.id === updated.id ? { ...it, ...updated } : it));
        }}
      />
    </div>
  );
}
